import { currentSubmissionContext, emitSubmissionTelemetry } from '@fabric-harness/sdk';
import type { FabricEvent, SubmissionTelemetrySink } from '@fabric-harness/sdk';

/**
 * Serving usage capture (v2 migration, workstream C7): attribute model/serving
 * spend to submissions through the ambient submission context.
 */
export interface ServingUsageCaptureOptions {
  /** Telemetry sink receiving `submission_usage` events (e.g. `databricksTelemetry().sink`). */
  sink: SubmissionTelemetrySink;
  /** Failure reporter for sink errors (default: silent, matching fire-and-forget telemetry). */
  onError?: (error: unknown) => void;
}

/**
 * Build an `onEvent` fan-in that turns model-usage events into
 * `submission_usage` telemetry. For every {@link FabricEvent} carrying
 * `data.usage` (`{ inputTokens?, outputTokens?, costUsd? }`, at least one
 * numeric field) — and optionally `data.model` — while an ambient
 * {@link currentSubmissionContext} is set, it emits one `submission_usage`
 * event stamped with the submission correlation (submission/attempt ids,
 * agent identity, tenant, actor).
 *
 * Events observed outside a submission context are dropped silently — not
 * every session turn belongs to a submission. The submission context does not
 * carry the submission kind, so emitted events use `kind: 'unknown'`.
 */
export function servingUsageCapture(options: ServingUsageCaptureOptions): (event: FabricEvent) => void {
  return (event) => {
    try {
      const usage = usageOf(event);
      if (!usage) return;
      const context = currentSubmissionContext();
      if (!context) return;
      const model = typeof event.data?.model === 'string' ? event.data.model : undefined;
      const at = Date.parse(event.timestamp);
      emitSubmissionTelemetry(
        options.sink,
        {
          type: 'submission_usage',
          submissionId: context.submissionId,
          attemptId: context.attemptId,
          kind: 'unknown',
          agent: context.agent,
          instanceId: context.instanceId,
          session: context.session,
          at: Number.isFinite(at) ? at : Date.now(),
          ...(context.tenantId !== undefined ? { tenantId: context.tenantId } : {}),
          ...(context.actor !== undefined ? { actor: context.actor } : {}),
          ...(model !== undefined ? { model } : {}),
          ...(usage.inputTokens !== undefined ? { inputTokens: usage.inputTokens } : {}),
          ...(usage.outputTokens !== undefined ? { outputTokens: usage.outputTokens } : {}),
          ...(usage.costUsd !== undefined ? { costUsd: usage.costUsd } : {}),
        },
        options.onError,
      );
    } catch (error) {
      options.onError?.(error);
    }
  };
}

interface UsageFields {
  inputTokens?: number;
  outputTokens?: number;
  costUsd?: number;
}

/** `data.usage` with at least one numeric field, else `undefined`. */
function usageOf(event: FabricEvent): UsageFields | undefined {
  const raw = event.data?.usage;
  if (typeof raw !== 'object' || raw === null || Array.isArray(raw)) return undefined;
  const usage = raw as Record<string, unknown>;
  const fields: UsageFields = {
    ...(typeof usage.inputTokens === 'number' ? { inputTokens: usage.inputTokens } : {}),
    ...(typeof usage.outputTokens === 'number' ? { outputTokens: usage.outputTokens } : {}),
    ...(typeof usage.costUsd === 'number' ? { costUsd: usage.costUsd } : {}),
  };
  return Object.keys(fields).length > 0 ? fields : undefined;
}

// ─── Inference-table joins ───────────────────────────────────────────────────

export interface InferenceTableUsageQueryOptions {
  /** Fully-qualified inference/payload table, `catalog.schema.table`. */
  fullTableName: string;
  /** Only aggregate requests at or after this ISO-8601 instant. */
  sinceIso?: string;
}

/**
 * SQL aggregating a serving endpoint's inference/payload table by
 * `client_request_id` — one row per submission with the request count and the
 * first/last request timestamps — for offline joins against
 * `fh_submission_telemetry.submission_id`.
 *
 * Join contract: callers MUST send the submission id as the serving request's
 * `client_request_id` (e.g. the `client_request_id` field on the
 * serving-endpoint invocation) — the inference table records it verbatim, and
 * this query's `submission_id` column only joins when that convention holds.
 * Rows without a `client_request_id` (traffic from other callers) are
 * excluded. Assumes the AI Gateway payload-table schema (`request_time`
 * timestamp column); adapt the projection for classic inference tables that
 * use `timestamp_ms`.
 */
export function inferenceTableUsageQuery(options: InferenceTableUsageQueryOptions): string {
  if (!/^[A-Za-z0-9_]+\.[A-Za-z0-9_]+\.[A-Za-z0-9_]+$/.test(options.fullTableName)) {
    throw new Error(
      `databricks serving-usage: fullTableName must be catalog.schema.table, got: ${options.fullTableName}`,
    );
  }
  let since = '';
  if (options.sinceIso !== undefined) {
    // Strict shape check first — Date.parse alone is far too lenient to gate a SQL literal.
    const isoShape = /^\d{4}-\d{2}-\d{2}T\d{2}:\d{2}:\d{2}(\.\d{1,3})?(Z|[+-]\d{2}:\d{2})$/;
    const parsed = Date.parse(options.sinceIso);
    if (!isoShape.test(options.sinceIso) || !Number.isFinite(parsed)) {
      throw new Error(`databricks serving-usage: sinceIso is not a valid ISO-8601 instant: ${options.sinceIso}`);
    }
    since = `\n  AND request_time >= TIMESTAMP '${new Date(parsed).toISOString()}'`;
  }
  return `SELECT
  client_request_id AS submission_id,
  COUNT(*) AS request_count,
  MIN(request_time) AS first_request_at,
  MAX(request_time) AS last_request_at
FROM ${options.fullTableName}
WHERE client_request_id IS NOT NULL${since}
GROUP BY client_request_id
ORDER BY last_request_at DESC`;
}
